import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Bot, Send, Loader2, Sparkles, RotateCcw, Mic, MicOff, Volume2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCopilotVoice } from "@/hooks/useCopilotVoice";
import { toast } from "sonner";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const SUGGESTED_QUESTIONS = [
  "Qual o resumo do estado atual do sistema?",
  "Quais alertas precisam de atenção imediata?",
  "Quem são os membros mais engajados?",
  "Quais grupos estão com baixa atividade?",
  "Sugira ações para melhorar o engajamento",
  "Há erros recentes nos logs?",
];

export default function Copilot() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const voice = useCopilotVoice({
    history: messages,
    onUserTranscript: (text) => setMessages((p) => [...p, { role: "user", content: text }]),
    onAssistantText: (text) => setMessages((p) => [...p, { role: "assistant", content: text }]),
    onError: (msg) => toast.error(`Voz: ${msg}`),
  });

  const voiceOn = voice.state !== "idle" && voice.state !== "error";

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (question?: string) => {
    const q = question ?? input.trim();
    if (!q || loading) return;

    const userMsg: Message = { role: "user", content: q };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput("");
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("admin-copilot", {
        body: { question: q, history: updated.slice(-6) },
      });
      if (error) throw error;
      const answer = data.answer as string;
      setMessages((prev) => [...prev, { role: "assistant", content: answer }]);
      // Se o modo voz está ativo, fala a resposta também
      if (voiceOn && answer) void voice.speak(answer);
    } catch (e: any) {
      setMessages((prev) => [...prev, { role: "assistant", content: `Erro: ${e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-headline flex items-center gap-2">
            <Bot className="h-6 w-6 text-primary" />
            Admin Copilot
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Assistente inteligente com acesso aos dados do sistema em tempo real.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {voiceOn && (
            <Badge variant="outline" className="gap-1.5">
              <span className={`w-2 h-2 rounded-full ${voice.state === "speaking" ? "bg-primary animate-pulse" : voice.state === "thinking" ? "bg-amber-500 animate-pulse" : "bg-emerald-500"}`} />
              {voice.state === "speaking" ? "Sarah falando" : voice.state === "thinking" ? "Pensando" : "Ouvindo"}
            </Badge>
          )}
          <Button
            variant={voiceOn ? "secondary" : "default"}
            size="sm"
            onClick={async () => {
              if (voiceOn) { voice.stop(); return; }
              try {
                await navigator.mediaDevices.getUserMedia({ audio: true });
                await voice.start();
                toast.success("Conversa por voz iniciada — pode falar");
              } catch {
                toast.error("Permissão de microfone negada");
              }
            }}
          >
            {voiceOn ? <><MicOff className="h-4 w-4 mr-2" /> Encerrar voz</> : <><Volume2 className="h-4 w-4 mr-2" /> Conversar por voz</>}
          </Button>
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chat Area */}
        <Card className="lg:col-span-3 flex flex-col" style={{ height: "calc(100vh - 220px)" }}>
          <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <Sparkles className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Olá! Sou o Copilot do SYRA</h3>
                  <p className="text-muted-foreground text-sm max-w-md">
                    Pergunte sobre métricas, alertas, membros ou peça recomendações.
                    Tenho acesso ao contexto atual do sistema.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <AnimatePresence initial={false}>
                    {messages.map((msg, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
                            msg.role === "user"
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-foreground"
                          }`}
                        >
                          {msg.content}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {loading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-muted rounded-2xl px-4 py-3 flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Analisando dados...
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="border-t p-4">
              <div className="flex gap-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Pergunte algo sobre o sistema..."
                  className="resize-none min-h-[44px] max-h-[120px]"
                  rows={1}
                  disabled={loading}
                />
                <Button onClick={() => sendMessage()} disabled={!input.trim() || loading} size="icon" className="shrink-0 h-[44px] w-[44px]">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Sugestões
              </h3>
              <div className="space-y-2">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    disabled={loading}
                    className="w-full text-left text-xs p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground disabled:opacity-50"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {messages.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setMessages([])}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-2" />
              Nova conversa
            </Button>
          )}

          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold mb-2">Sobre o Copilot</h3>
              <p className="text-xs text-muted-foreground">
                O Copilot tem acesso em tempo real a métricas, alertas, membros, grupos e logs do sistema. Use-o para obter insights rápidos e tomar decisões informadas.
              </p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {["Métricas", "Alertas", "Membros", "Grupos", "Logs"].map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-[10px]">{tag}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
